import { Box, Button, IconButton, Tooltip, Typography } from '@material-ui/core';
import _ from 'lodash';
import { FunctionComponent, useMemo, useState } from 'react';

import { ReactComponent as BlockerIcon } from '../../../../assets/icons/blocker.svg';
import { ReactComponent as CloseFilledIcon } from '../../../../assets/icons/close-filled.svg';
import { ReactComponent as DiscussionNotification } from '../../../../assets/icons/discussion-notification.svg';
import { ReactComponent as DownloadIcon } from '../../../../assets/icons/download.svg';
import { ReactComponent as InformationIcon } from '../../../../assets/icons/information.svg';
import { useExtensionDownload } from '../../../../hooks/useExtensionDownload';
import InformationTooltip from '../../../../shared/informationTooltip';
import { ContributorReviewSteps, ContributorWorkSteps } from '../../../../types/contributorWork';
import { WorkItem, WorkType } from '../../../../types/work';
import ExtensionOpenerItem from './ExtensionOpenerItem';
import InstructionsModal from './InstructionsModal';
import { useStyles } from './styled';

export type Props = {
    name?: string;
    workInProgressOnClick?: () => void;
    questionsOnClick?: () => void;
    reportBlockerOnClick?: () => void;
    instructionsOnClick?: () => void;
    currentStep: ContributorWorkSteps | ContributorReviewSteps;
    workToken: string | null;
    conversationNotification: boolean;
    workType?: WorkType;
    work?: WorkItem;
};

const Header: FunctionComponent<Props> = ({
    name,
    workInProgressOnClick,
    questionsOnClick,
    reportBlockerOnClick,
    currentStep,
    workToken,
    conversationNotification,
    workType,
    work
}: Props) => {
    const classes = useStyles();
    const { downloadExtension } = useExtensionDownload();
    const [ isInstructionsOpen, setIsInstructionsOpen ] = useState(false);
    const hasAssociatedBlocker = useMemo(() => {
        return !!work?.workInput?.['associated_blocker_task_title'];
    }, [ work?.workInput ]);

    const workTypeText = useMemo(() => {
        const type = workType || work?.workType;
        if (!type) {
            return '';
        }
        
        if (work?.workInput?.['associated_blocker_task_title']){
            return 'Resolve blocker';
        }

        // Special case for CODING type if needed, otherwise format the enum string
        if (type === WorkType.CODING && work?.taskType) {
            return work.taskType;
        }

        // Converts "CREATE_COMPONENT" -> "Create Component"
        return _.startCase(_.toLower(WorkType[type]));
    }, [ workType, work ]);

    const handleDownloadExtension = () => {
        downloadExtension();
    };

    return (
        <Box className={classes.root}>
            <Box className={classes.titleContainer}>
                <Tooltip 
                    title={name || ''} 
                    arrow
                    classes={{ tooltip: classes.tooltip }}
                >
                    <Typography className={classes.title}>{name}</Typography>
                </Tooltip>
                {currentStep !== ContributorWorkSteps.TaskAccept && (
                    <>
                        <Box 
                            className={classes.workInstructionsBox}
                        >
                            <Typography className={classes.workInstructionsText}>
                                {workTypeText}
                            </Typography>
                            <Box className="work-instructions-actions">
                                <InformationTooltip text="work instructions" placement="top">
                                    <IconButton
                                        className="action-icon-btn"
                                        onClick={() => setIsInstructionsOpen(true)}
                                    >
                                        <InformationIcon />
                                    </IconButton>
                                </InformationTooltip>
                            </Box>
                        </Box>
                        <Box className="work-in-progress-btn">
                            <img src="clock.svg" className="clock-icon" />
                            <Typography className={classes.workInProgressText}>
                                    Work in progress
                            </Typography>
                            <Box className="work-in-progress-actions">
                                {workToken && (
                                    <>
                                        <InformationTooltip text="Download Extension" placement="top">
                                            <IconButton
                                                className="action-icon-btn"
                                                onClick={handleDownloadExtension}
                                            >
                                                <DownloadIcon />
                                            </IconButton>
                                        </InformationTooltip>
                                        <ExtensionOpenerItem workToken={workToken} />
                                    </>
                                )}
                                {reportBlockerOnClick && !hasAssociatedBlocker && (
                                    <InformationTooltip text="Report blocker" placement="top">
                                        <IconButton
                                            className="action-icon-btn"
                                            onClick={reportBlockerOnClick}
                                        >
                                            <BlockerIcon />
                                        </IconButton>
                                    </InformationTooltip>
                                )}
                                {workInProgressOnClick && (
                                    <InformationTooltip text="Cancel" placement="top">
                                        <IconButton
                                            className="action-icon-btn"
                                            onClick={workInProgressOnClick}
                                        >
                                            <CloseFilledIcon />
                                        </IconButton>
                                    </InformationTooltip>
                                )}
                            </Box>
                        </Box>
                    </>
                )}
            </Box>
            {currentStep === ContributorWorkSteps.WorkingOnPullRequest && (
                <Box style={{ position: 'relative' }}>
                    <Button className="questions-btn" onClick={questionsOnClick}>
                        <img src="chat.svg" alt="Chat" />
                        <Typography className={classes.questionsText}>
                            Work discussion
                        </Typography>
                    </Button>
                    {conversationNotification && (
                        <DiscussionNotification className={classes.discussionNotification} />
                    )}
                </Box>
            )}
            <InstructionsModal 
                open={isInstructionsOpen}
                onClose={() => setIsInstructionsOpen(false)}
                workType={workType}
                work={work}
            />
        </Box>
    );
};

export default Header;
